import { Box, Button, Paper, Typography, CircularProgress } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import { putIncomingMail, getMailflowStatus } from "../api/APICalls";
import { useState } from "react";
import MailStatusDisplay from "./MailStatusDisplay";
import { useQuery } from "@tanstack/react-query";

const IncomingMailFlow = () => {
  const [token, setToken] = useState(null);
  const [isTriggering, setIsTriggering] = useState(false);

  const { data: mailStatus, isFetching } = useQuery({
    queryKey: ["mailStatus", token],
    queryFn: () => getMailflowStatus(token),
    refetchInterval: (query) => {
      const status = query.state.data;
      if (status?.mail_received && status.mail_received === "Success") {
        return false;
      }
      return 3000;
    },
    enabled: !!token,
  });

  const handleIncomingClick = async () => {
    const domain = localStorage.getItem("domain");
    setIsTriggering(true);
    setToken(null);
    try {
      const result = await putIncomingMail(domain);
      if (result.token) {
        setToken(result.token);
      }
    } catch (error) {
      console.error(error);
      alert(error.message || "Failed to check incoming mail flow");
    } finally {
      setIsTriggering(false);
    }
  };

  const isTestCompleted = mailStatus?.mail_received === "Success";
  const isPending = !!token && !isTestCompleted;

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 4, width: "100%", boxSizing: "border-box" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 2, sm: 3 },
          width: "100%",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: "secondary.main", fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
          Incoming Mail Flow
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
          Test the ability of your domain to receive emails from external sources.
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          size="large"
          onClick={handleIncomingClick}
          disabled={isTriggering || isPending || isTestCompleted}
          startIcon={isTriggering || isPending ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
          sx={{ 
            borderRadius: 8, 
            px: { xs: 3, sm: 6 }, 
            py: 1.5,
            textTransform: "none",
            fontSize: { xs: "0.9rem", sm: "1.1rem" },
          }}
        >
          {isTriggering ? "Initializing..." : isPending ? "In Progress..." : isTestCompleted ? "Success" : "Start Test"}
        </Button>
        
        {token && (
          <MailStatusDisplay data={mailStatus} isLoading={isFetching} />
        )}

        {!token && (
          <Box sx={{ 
            mt: 2, 
            p: 3, 
            border: "1px dashed", 
            borderColor: "divider", 
            borderRadius: 2,
            bgcolor: "rgba(0,0,0,0.02)",
            textAlign: "center"
          }}>
            <Typography variant="body2" color="text.secondary">
              Click the button above to begin the automated mail flow test
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default IncomingMailFlow;
